import { useEffect, useRef, useState } from 'react';

const plans = [
  {
    name: 'Starter',
    price: '$750',
    period: '/month',
    color: '#00d4ff',
    plays: '2,000+',
    badge: null,
    features: [
      { text: '2,000+ ad plays per month', highlight: true },
      { text: 'Standard Tampa routes', highlight: false },
      { text: 'Daytime coverage', highlight: false },
      { text: 'Weekly campaign report', highlight: false },
      { text: '1 creative included', highlight: false },
    ],
  },
  {
    name: 'Professional',
    price: '$1,500',
    period: '/month',
    color: '#39ff14',
    plays: '4,000+',
    badge: 'MOST POPULAR',
    features: [
      { text: '4,000+ ad plays per month', highlight: true },
      { text: 'Premium Tampa routes', highlight: false },
      { text: 'Day & night coverage', highlight: false },
      { text: 'Real-time tracking', highlight: false },
      { text: 'Priority support', highlight: false },
      { text: '2 creatives included', highlight: false },
    ],
  },
  {
    name: 'Campaign',
    price: '$150',
    period: '/hour',
    color: '#b026ff',
    plays: 'Custom',
    badge: null,
    features: [
      { text: '4-hour minimum booking', highlight: true },
      { text: 'Custom routes & timing', highlight: false },
      { text: 'Event & promotion targeting', highlight: false },
      { text: 'Flexible scheduling', highlight: false },
      { text: 'Campaign highlights report', highlight: false },
    ],
  },
];

const Pricing = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="pricing" className="relative py-24 md:py-32 bg-dark-charcoal overflow-hidden" ref={sectionRef}>
      {/* BG Radial */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, rgba(57,255,20,0.04) 0%, transparent 60%)' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div
          className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div
            className="inline-block text-xs font-rajdhani font-bold tracking-widest uppercase mb-4 px-3 py-1 rounded-full"
            style={{ color: '#39ff14', border: '1px solid #39ff14', boxShadow: '0 0 10px rgba(57,255,20,0.3)' }}
          >
            Pricing
          </div>
          <h2 className="font-orbitron font-black text-4xl md:text-5xl lg:text-6xl text-white">
            FLEXIBLE CAMPAIGNS FOR{' '}
            <span style={{ color: '#39ff14', textShadow: '0 0 20px #39ff14' }}>EVERY BUDGET</span>
          </h2>
          <p className="mt-4 text-gray-400 text-lg font-rajdhani">
            Maximum exposure without breaking the bank
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-start">
          {plans.map((plan, i) => (
            <div
              key={plan.name}
              className={`relative rounded-3xl bg-dark-card p-8 transition-all duration-500 hover:scale-105 cursor-pointer ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              } ${plan.badge ? 'ring-2' : ''}`}
              style={{
                border: `2px solid ${plan.color}30`,
                transitionDelay: `${i * 120}ms`,
                ...(plan.badge ? { ringColor: plan.color } : {}),
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.borderColor = plan.color;
                (e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 40px ${plan.color}25`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.borderColor = `${plan.color}30`;
                (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
              }}
            >
              {/* Most Popular Badge */}
              {plan.badge && (
                <div
                  className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-rajdhani font-black tracking-widest uppercase text-black"
                  style={{ backgroundColor: plan.color, boxShadow: `0 0 20px ${plan.color}` }}
                >
                  {plan.badge}
                </div>
              )}

              {/* Plan Name */}
              <div className="font-rajdhani font-bold text-lg tracking-widest uppercase text-gray-400 mb-2">
                {plan.name}
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-1 mb-1">
                <span
                  className="font-orbitron font-black text-5xl md:text-6xl"
                  style={{ color: plan.color, textShadow: `0 0 20px ${plan.color}` }}
                >
                  {plan.price}
                </span>
                <span className="text-gray-500 text-lg font-rajdhani">{plan.period}</span>
              </div>

              <div
                className="inline-block px-3 py-1 rounded-full text-xs font-rajdhani font-bold mb-6"
                style={{ backgroundColor: `${plan.color}15`, color: plan.color }}
              >
                {plan.plays} plays
              </div>

              {/* Divider */}
              <div className="w-full h-px mb-6" style={{ backgroundColor: `${plan.color}20` }} />

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feat) => (
                  <li key={feat.text} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-check-line text-sm" style={{ color: plan.color }} />
                    </div>
                    <span
                      className={`text-sm leading-snug ${feat.highlight ? 'font-semibold' : ''}`}
                      style={{ color: feat.highlight ? plan.color : '#9ca3af' }}
                    >
                      {feat.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <a
                href="#contact"
                className="block w-full py-3.5 rounded-full font-rajdhani font-bold tracking-widest uppercase text-sm text-center transition-all duration-300 whitespace-nowrap"
                style={{
                  border: `2px solid ${plan.color}`,
                  color: plan.color,
                  backgroundColor: 'transparent',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.backgroundColor = plan.color;
                  (e.currentTarget as HTMLAnchorElement).style.color = '#000';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLAnchorElement).style.color = plan.color;
                }}
              >
                Get Started
              </a>
            </div>
          ))}
        </div>

        {/* Guarantee */}
        <div
          className={`text-center mt-12 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div
            className="inline-flex items-center gap-3 px-6 py-3 rounded-full text-sm font-rajdhani font-semibold"
            style={{
              backgroundColor: 'rgba(57,255,20,0.08)',
              border: '1px solid rgba(57,255,20,0.3)',
              color: '#39ff14',
            }}
          >
            <i className="ri-shield-check-line text-lg" />
            Best Price Per Impression Guaranteed — Or We Beat Any Competitor
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
